import { useState, useEffect } from "react";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Empty, PageHeader } from "@/components/Shell";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CalendarCheck, Save, Info } from "lucide-react";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const CAT_FR = { Cleaning: "Entretien", Security: "Sécurité", Cook: "Cuisine", Electrician: "Électricien", Contractor: "Prestataire", Chauffeur: "Chauffeur" };

export default function TeacherAttendance({ canManage }) {
  const { L } = useLang();
  const [period, setPeriod] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = async (p) => {
    setLoading(true); setEdits({});
    try { setData(await api.get("/teacher-attendance", { params: { period: p } })); }
    catch (e) { toast.error(L("Failed to load", "Échec du chargement")); }
    setLoading(false);
  };
  useEffect(() => { load(period); }, [period]);

  const val = (r, field) => edits[r.teacher_id]?.[field] ?? String(r[field] ?? 0);
  const setVal = (r, field, v) => setEdits((e) => ({ ...e, [r.teacher_id]: { ...e[r.teacher_id], [field]: v } }));
  const suggested = (r) => {
    const abs = Number(val(r, "absences") || 0);
    return (r.daily_rate * abs);
  };

  const save = async (r) => {
    setBusy(r.teacher_id);
    try {
      await api.post("/teacher-attendance", {
        teacher_id: r.teacher_id, period,
        absences: parseInt(val(r, "absences") || "0", 10),
        lates: parseInt(val(r, "lates") || "0", 10),
        note: edits[r.teacher_id]?.note ?? r.note ?? "",
      });
      toast.success(L("Saved", "Enregistré"));
      await load(period);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy("");
  };

  const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (loading) return <Empty title={L("Loading…", "Chargement…")} />;

  return (
    <div className="animate-fade-up" data-testid="teacher-attendance-page">
      <PageHeader title={L("Staff Attendance", "Assiduité du personnel")}
        description={L("Monthly count of absences and lateness per staff member — teachers and other personnel.", "Compteur mensuel des absences et retards par membre du personnel — enseignants et autres personnels.")}
        action={<Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="w-44" data-testid="attendance-period" />} />

      <div className="flex items-start gap-2 rounded-md bg-secondary/60 border border-border p-3 mb-5 text-sm" data-testid="attendance-info">
        <Info className="h-4 w-4 mt-0.5 text-primary shrink-0" />
        <span>{L(`Working days: ${data?.working_days || 20}/month. Public holidays and vacation are paid (not deducted). Suggested deduction = absences × (monthly salary ÷ ${data?.working_days || 20}).`,
          `Jours ouvrables : ${data?.working_days || 20}/mois. Les jours fériés et les vacances sont payés (non déduits). Déduction suggérée = absences × (salaire mensuel ÷ ${data?.working_days || 20}).`)}</span>
      </div>

      {(data?.rows || []).length === 0 ? <Empty title={L("No staff yet", "Aucun membre du personnel")} /> : (
        <Table>
          <TableHeader><TableRow>
            <TableHead>{L("Name", "Nom")}</TableHead>
            <TableHead>{L("Role / Category", "Rôle / Catégorie")}</TableHead>
            <TableHead>{L("Monthly salary", "Salaire mensuel")}</TableHead>
            <TableHead className="w-28">{L("Absences", "Absences")}</TableHead>
            <TableHead className="w-28">{L("Lates", "Retards")}</TableHead>
            <TableHead>{L("Suggested deduction", "Déduction suggérée")}</TableHead>
            <TableHead>{L("Note", "Note")}</TableHead>
            {canManage && <TableHead></TableHead>}
          </TableRow></TableHeader>
          <TableBody>{data.rows.map((r) => (
            <TableRow key={r.teacher_id} data-testid={`attendance-row-${r.teacher_id}`}>
              <TableCell className="font-medium">{r.teacher_name}</TableCell>
              <TableCell className="text-sm text-muted-foreground" data-testid={`attendance-cat-${r.teacher_id}`}>{r.kind === "personnel" ? L(r.category || "Staff", CAT_FR[r.category] || r.category || "Personnel") : L("Teacher", "Enseignant")}</TableCell>
              <TableCell className="text-sm">{r.monthly_salary != null ? usd(r.monthly_salary) : "—"}</TableCell>
              <TableCell><Input type="number" min="0" className="w-20" value={val(r, "absences")} onChange={(e) => setVal(r, "absences", e.target.value)} disabled={!canManage} data-testid={`attendance-absences-${r.teacher_id}`} /></TableCell>
              <TableCell><Input type="number" min="0" className="w-20" value={val(r, "lates")} onChange={(e) => setVal(r, "lates", e.target.value)} disabled={!canManage} data-testid={`attendance-lates-${r.teacher_id}`} /></TableCell>
              <TableCell className="font-semibold text-destructive" data-testid={`attendance-deduction-${r.teacher_id}`}>{r.monthly_salary != null ? usd(suggested(r)) : "—"}</TableCell>
              <TableCell><Input value={edits[r.teacher_id]?.note ?? r.note ?? ""} onChange={(e) => setVal(r, "note", e.target.value)} disabled={!canManage} className="min-w-[140px]" placeholder={L("optional", "optionnel")} /></TableCell>
              {canManage && <TableCell><Button size="sm" onClick={() => save(r)} disabled={busy === r.teacher_id} data-testid={`attendance-save-${r.teacher_id}`}><Save className="h-4 w-4 mr-1" />{L("Save", "Enregistrer")}</Button></TableCell>}
            </TableRow>))}</TableBody>
        </Table>
      )}
    </div>
  );
}
